<script setup lang="ts">
import { computed, useSlots } from 'vue'

const props = defineProps({
  loading: { type: Boolean, default: false },
  text: { type: String, required: false, default: '' },
  size: {
    default: 'md',
    validator: (value: string) =>
      ['custom','sm','md','lg','xl','2xl','long'].includes(value),
  },
  image: { type: String, required: false, default: '' },
  contextualStyles: { type: Boolean, default: false },
})

const extraClasses = computed(() => {
  switch (props.size) {
    case 'custom': return ['']
    case 'sm':     return ['px-4 py-1 text-xs', props.text ? 'leading-4' : 'leading-none']
    case 'md':     return ['px-6 py-1 text-sm', props.text ? 'leading-6' : 'leading-none']
    case 'lg':     return ['px-6 py-[7px] text-lg', props.text ? 'leading-6' : 'leading-none']
    case 'xl':     return ['px-8 py-[15px] text-lg', props.text ? 'leading-6' : 'leading-none']
    case '2xl':    return ['px-16 py-[31px] text-xl', props.text ? 'leading-6' : 'leading-none']
    case 'long':   return ['w-48 px-6 py-1 text-sm', props.text ? 'leading-6' : 'leading-none']
    default:       return ['px-6 py-1 text-sm', props.text ? 'leading-6' : 'leading-none']
  }
})

const contextualClasses = computed(() =>
  props.contextualStyles ? 'btn-cancel--contextual' : ''
)

const slots = useSlots()
const hasPrefix = computed(() => !!slots['image-prefix'])
const hasSuffix = computed(() => !!slots['image-suffix'])
const hasTextSlot = computed(() => !!slots['text'])
const hasText = computed(() => !!props.text || hasTextSlot.value)

// Add horizontal gap only if there’s more than one visible piece
const hasMultipleParts = computed(() => {
  const parts = [
    hasPrefix.value,
    hasText.value,
    hasSuffix.value,
  ].filter(Boolean)
  return parts.length > 1
})
</script>

<template>
  <button
    type="submit"
    class="group enabled:opacity-100 opacity-50 flex items-center justify-center font-semibold rounded enabled:bg-transparent bg-transparent enabled:cursor-pointer cursor-not-allowed transition-all duration-300 btn-cancel"
    :class="[extraClasses, contextualClasses]"
  >
    <NuxtIcon name="svg-spinners:ring-resize" v-if="loading" class="w-12 h-5" />

    <template v-else>
      <div
        class="flex items-center uppercase whitespace-nowrap"
        :class="hasMultipleParts ? 'gap-2' : ''"
      >
        <div v-if="hasPrefix" class="flex items-center text-current">
          <slot name="image-prefix" />
        </div>

        <slot name="text">
          <p v-if="text">{{ text }}</p>
        </slot>

        <div v-if="hasSuffix" class="flex items-center text-current">
          <slot name="image-suffix" />
        </div>
      </div>
    </template>
  </button>
</template>
